/**
 * Route table — single source of truth for view ↔ URL path mapping (B-048).
 *
 * The app previously switched views on React state with an optional ?view= query
 * param (validation harness + old shared links). Both now resolve through here:
 *
 *   - Clean paths are canonical: /panchanga/daily, /jyotish/birth-chart, …
 *   - Legacy `/?view=<ViewType>` renders the same view WITHOUT a redirect, so the
 *     50 golden-reference screenshot URLs (`?date=&view=&locale=&time=`) keep
 *     producing byte-identical sessions — no navigation event, no pixel drift.
 *   - Unknown paths fall back to the landing view (CloudFront already rewrites
 *     403/404 → /index.html, so any deep link reaches the SPA).
 *
 * View ids double as analytics page names (see CLAUDE.md impact warning) — the
 * mapping changes URLs only, never the ViewType strings.
 */
import type { ViewType } from './components/NavigationBanner/NavigationBanner';

export const VIEW_PATHS: Record<ViewType, string> = {
  'landing': '/',
  'daily-panchanga': '/panchanga/daily',
  'monthly-panchanga': '/panchanga/monthly',
  'calendar-download': '/panchanga/download',
  'planetary-alignment': '/planets/kala-chakra',
  'full-chart': '/planets/jyotisha-drishti',
  'planetary-positions': '/planets/positions',
  'sky-simulator': '/sky',
  'birth-chart': '/jyotish/birth-chart',
  'muhurtha': '/jyotish/shubha-kala',
  'daily-best-times': '/jyotish/daily-best-times',
  'mahabharata-dating': '/datings/mahabharata',
  'ramayana-dating': '/datings/ramayana',
  'buddha-dating': '/datings/buddha',
  'learnings': '/learn',
  'dhyana': '/dhyana',
  'privacy': '/privacy',
  'terms': '/terms',
  'support': '/support',
};

const PATH_VIEWS: Record<string, ViewType> = Object.fromEntries(
  Object.entries(VIEW_PATHS).map(([view, path]) => [path, view as ViewType]),
) as Record<string, ViewType>;

const VALID_VIEWS = new Set<string>(Object.keys(VIEW_PATHS));

/** Canonical path for a view; Jñāna Kośa articles get /learn/:articleId. */
export function pathForView(view: ViewType, articleId?: string): string {
  if (view === 'learnings' && articleId) return `/learn/${articleId}`;
  return VIEW_PATHS[view];
}

export interface ResolvedRoute {
  view: ViewType;
  articleId?: string;
}

/**
 * Resolve a location to a view. Precedence:
 *   1. A known clean path (incl. /learn/:articleId).
 *   2. On "/" only: the legacy ?view= param (validation harness / old links).
 *   3. Landing.
 */
export function resolveRoute(pathname: string, search: string): ResolvedRoute {
  const path = pathname.replace(/\/+$/, '') || '/';

  const articleMatch = path.match(/^\/learn\/([^/]+)$/);
  if (articleMatch) return { view: 'learnings', articleId: decodeURIComponent(articleMatch[1]) };

  // Legacy ?view= must win on "/" — PATH_VIEWS maps "/" to landing, so checking
  // it first would shadow every harness/legacy URL.
  if (path === '/') {
    const legacy = new URLSearchParams(search).get('view');
    if (legacy && VALID_VIEWS.has(legacy)) return { view: legacy as ViewType };
    return { view: 'landing' };
  }

  const byPath = PATH_VIEWS[path];
  if (byPath) return { view: byPath };

  return { view: 'landing' };
}
